import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { HpRespFeaturedModuleItem } from './HpRespFeaturedModuleItem';
import styles from './HpRespFeaturedModule.scss';
import { trackFeaturedImpressions, trackFeaturedClick } from './helpers/FeaturedTracking';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';

const handleInitialDisplay = ({ inViewport, items, primaryItem }) => {
    if (primaryItem && inViewport) {
        trackFeaturedImpressions([primaryItem, ...items]);
    }
};

const HpRespFeaturedModuleComponent = ({
    componentModule,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
}) => {
    if (!componentModule) {
        return null;
    }

    const { items, primaryItem } = componentModule;
    const primaryImageHeight = primaryItem.mobileImageHeight || primaryItem.imageHeight;
    const primaryImageWidth = primaryItem.mobileImageWidth || primaryItem.imageWidth;
    const primaryImageUrl = primaryItem.mobileImageUrl || primaryItem.imageUrl;

    return (
        <HpRespModuleContainer addTopGap>
            <SharedInViewport
                stopWhenInViewport
                onInViewportChange={({ inViewport }) =>
                    handleInitialDisplay({ inViewport, items, primaryItem })
                }
            >
                <div className="rowFlex">
                    <div className={`${styles.primaryItem} colXs12 colLg6`}>
                        <SeoLink
                            className={styles.primaryLink}
                            linkData={primaryItem.linkData}
                            ariaLabel={primaryItem.cta}
                            onClick={() => {
                                trackFeaturedClick(primaryItem, 0);
                                trackModuleLocation({
                                    label: 'five block module',
                                    moduleIndex,
                                    totalModules,
                                });
                            }}
                        >
                            <HpLazyImageWrapper
                                useLazyLoad={useLazyLoadImages}
                                imageHeight={primaryImageHeight}
                                imageWidth={primaryImageWidth}
                            >
                                <ResponsiveImage
                                    className={styles.primaryImage}
                                    src={primaryImageUrl}
                                    altText={primaryItem.title}
                                    sizes="(max-width:768px) 95vw, 50vw"
                                    srcSetWidths={[400, 640, 800, 960, 1200]}
                                />
                            </HpLazyImageWrapper>
                        </SeoLink>
                    </div>

                    <div className={`${styles.productItems} colXs12 colLg6`}>
                        <div className={`${styles.fullHeight} rowFlex`}>
                            {items.map((item, index) => (
                                <HpRespFeaturedModuleItem
                                    key={`${item?.linkData?.path}-${index}`}
                                    item={item}
                                    useLazyLoadImages={useLazyLoadImages}
                                    onClick={() => {
                                        /**
                                         * Index incremented by 1 because primaryItem is using `position: 1`
                                         */
                                        trackFeaturedClick(item, index + 1);
                                        trackModuleLocation({
                                            label: 'five block module',
                                            moduleIndex,
                                            totalModules,
                                        });
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </SharedInViewport>
        </HpRespModuleContainer>
    );
};

HpRespFeaturedModuleComponent.propTypes = {
    componentModule: PropTypes.object.isRequired,
    useLazyLoadImages: PropTypes.bool,
    moduleIndex: PropTypes.number,
    totalModules: PropTypes.number,
};

export const HpRespFeaturedModule = createFragmentContainer(HpRespFeaturedModuleComponent, {
    componentModule: graphql`
        fragment HpRespFeaturedModule_componentModule on FeaturedModule {
            primaryItem {
                cta
                imageUrl
                imageHeight
                imageWidth
                ... @include(if: $isMobile) {
                    mobileImageHeight
                    mobileImageWidth
                    mobileImageUrl
                }
                title
                linkData {
                    # FeaturedTracking.js
                    path
                    ...SeoLink_linkData
                }
            }
            items {
                cmsId
                cta
                imageUrl
                title
                linkData {
                    # FeaturedTracking.js
                    path
                    ...SeoLink_linkData
                }
            }
        }
    `,
});
